import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BarChart2, HardDrive, AlertTriangle, CheckCircle, Smartphone, Monitor, Usb, Zap, Target, Timer, ThumbsUp, Printer, MousePointer, Link, Shield, Camera, DollarSign } from 'lucide-react';
import { Users, TrendingUp, Download, Activity, Keyboard, Wifi, FileText, Clock, ChevronDown, ChevronUp, RefreshCw, Box } from '../components/icons';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import api, { reportesAPI } from '../services/api';
import ResenasAgenteModal from '../components/ResenasAgenteModal';
import ReporteSeguridad from '../components/ReporteSeguridad';
import TicketsAgentePanel from '../components/TicketsAgentePanel';

// YYYY-MM-DD y YYYY-MM con la fecha LOCAL (toISOString usaría UTC y en la tarde-noche de Chile cae en el día siguiente).
const dosDig = (n) => String(n).padStart(2, '0');
const fechaLocal = (d) => `${d.getFullYear()}-${dosDig(d.getMonth() + 1)}-${dosDig(d.getDate())}`;
const mesLocal = (d) => `${d.getFullYear()}-${dosDig(d.getMonth() + 1)}`;


const COLORS_PIE = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
const COLORS_ESTADO = {
    'Pendiente': '#f59e0b', 'Asignado': '#3b82f6', 'En Proceso': '#f97316',
    'Esperando Usuario': '#a855f7', 'Resuelto': '#22c55e', 'Cerrado': '#6b7280', 'Cancelado': '#ef4444'
};

// ── Widget wrapper ──────────────────────────────────────────────────
function Widget({ title, icon: Icon, children, className = '', span = 1, action }) {
    const spanClass = span === 2 ? 'lg:col-span-2' : span === 3 ? 'lg:col-span-3' : '';
    return (
        <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow flex flex-col ${spanClass} ${className}`}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                    {Icon && <Icon className="w-4 h-4 text-primary" />}
                    {title}
                </h3>
                {action}
            </div>
            <div className="p-5 flex-1 flex flex-col justify-center">{children}</div>
        </div>
    );
}

// ── KPI Card ────────────────────────────────────────────────────────
const clp = (n) => `$${Math.round(Number(n) || 0).toLocaleString('es-CL')}`;

function KpiCard({ label, value, icon: Icon, color = 'blue', sub }) {
    const bg = {
        blue: 'bg-blue-50 dark:bg-blue-900/20', green: 'bg-emerald-50 dark:bg-emerald-900/20',
        yellow: 'bg-amber-50 dark:bg-amber-900/20', purple: 'bg-purple-50 dark:bg-purple-900/20',
        red: 'bg-red-50 dark:bg-red-900/20', indigo: 'bg-indigo-50 dark:bg-indigo-900/20',
    };
    const text = {
        blue: 'text-blue-600', green: 'text-emerald-600', yellow: 'text-amber-600',
        purple: 'text-purple-600', red: 'text-red-600', indigo: 'text-indigo-600',
    };
    return (
        <div className={`${bg[color] || bg.blue} rounded-xl p-4 flex items-center gap-4`}>
            <div className={`p-3 rounded-lg ${text[color] || text.blue} bg-white/60 dark:bg-black/10`}>
                <Icon className="w-6 h-6" />
            </div>
            <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
                {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

// ── Custom Tooltip ──────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-sm">
            <p className="font-medium text-gray-900 dark:text-white mb-1">{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.color }} className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
                    {p.name}: <span className="font-semibold">{p.value}</span>
                </p>
            ))}
        </div>
    );
}

// ── SLA Gauge ───────────────────────────────────────────────────────
function SlaGauge({ label, value, icon: Icon }) {
    const color = value >= 90 ? '#22c55e' : value >= 70 ? '#f59e0b' : '#ef4444';
    const circumference = 2 * Math.PI * 40;
    const offset = circumference - (value / 100) * circumference;
    return (
        <div className="flex flex-col items-center">
            <div className="relative w-24 h-24">
                <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="none" className="text-gray-200 dark:text-gray-700" />
                    <circle cx="50" cy="50" r="40" stroke={color} strokeWidth="8" fill="none"
                        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                        style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-gray-900 dark:text-white">{value}%</span>
                </div>
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {Icon && <Icon className="w-3.5 h-3.5" />} {label}
            </div>
        </div>
    );
}

// R-2: module-level constant — icons never change, no need to recreate per render
const catIcons = {
    'Equipos': HardDrive, 'Móviles': Smartphone, 'Monitores': Monitor,
    'Periféricos': Keyboard, 'Accesorios': Usb, 'Red': Wifi,
    'Toner': Printer, 'Papelería': FileText, 'Cables': Link,
    'Insumos': Box, 'Notebook': HardDrive, 'Desktop': Monitor, 'Otros': Box
};

// ── Main Component ──────────────────────────────────────────────────
export default function Reportes() {
    const navigate = useNavigate();
    const { token } = useAuth();

    useEffect(() => { if (!token) navigate('/login'); }, [token, navigate]);

    const [loading, setLoading] = useState(true);
    const [metadata, setMetadata] = useState({ activos: { categorias: [], estados: [] }, usuarios: { empresas: [], departamentos: [] }, tickets: { estados: [] } });
    const [summary, setSummary] = useState(null);
    const [tiPerf, setTiPerf] = useState(null);
    const [agenteResenas, setAgenteResenas] = useState(null);
    const [agenteExpandido, setAgenteExpandido] = useState(null);
    const [tiLoading, setTiLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [generatingTrimestral, setGeneratingTrimestral] = useState(false);
    // Periodo: «Total» (todo el historial, por defecto), un mes puntual o un rango de fechas a elección.
    const [periodo, setPeriodo] = useState('total');
    const [mes, setMes] = useState(mesLocal(new Date()));
    const [rango, setRango] = useState({
        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    // Total = sin fechas: cada reporte toma todo lo que hay. Mes = del 1 al último día de ese mes.
    const dateRange = useMemo(() => {
        if (periodo === 'total') return { start: '', end: '' };
        if (periodo === 'mes') {
            const [y, m] = mes.split('-').map(Number);
            return { start: `${mes}-01`, end: fechaLocal(new Date(y, m, 0)) };
        }
        return rango;
    }, [periodo, mes, rango]);
    const desdeTi = periodo === 'total' ? 'total' : dateRange.start; // «total» le pide al servidor partir desde el primer ticket
    const [filters, setFilters] = useState({ empresa: 'all' });
    const [trimestralParams, setTrimestralParams] = useState({
        trimestre: Math.floor(new Date().getMonth() / 3) + 1,
        año: new Date().getFullYear()
    });

    // ── Data Loading ─────────────────────────────────────────────
    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [metaRes, summaryRes] = await Promise.all([
                api.get('/reportes/metadata').catch(() => ({ data: metadata })),
                api.get('/reportes/summary', {
                    params: { startDate: dateRange.start, endDate: dateRange.end, ...filters }
                })
            ]);
            setMetadata(metaRes.data);
            setSummary(summaryRes.data);
        } catch (e) {
            console.error('Error loading reportes:', e);
        } finally {
            setLoading(false);
        }
    }, [dateRange.start, dateRange.end, filters]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadTi = useCallback(async () => {
        setTiLoading(true);
        try {
            const { data } = await reportesAPI.getTiPerformance({ from: desdeTi, to: dateRange.end });
            setTiPerf(data);
        } catch (e) {
            console.error('Error TI:', e);
        } finally {
            setTiLoading(false);
        }
    }, [desdeTi, dateRange.end]);

    // R-1: two separate effects so a change that only affects loadTi doesn't also re-run loadAll
    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => { loadTi(); }, [loadTi]);

    // ── Computed Data ────────────────────────────────────────────
    const stats = useMemo(() => {
        if (!summary) return null;
        const e = summary.estadisticas || {};
        return {
            totalUsuarios: e.totalUsuarios || 0,
            totalActivos: e.totalActivos || 0,
            activosAsignados: e.activosAsignados || 0,
            activosDisponibles: e.activosDisponibles || 0,
            totalTickets: e.totalTickets || 0,
        };
    }, [summary]);

    const catData = useMemo(() =>
        (summary?.activos?.porCategoria || []).map(x => ({ name: x.categoria, value: x.cantidad })),
        [summary]);

    const ticketEstadoData = useMemo(() =>
        (summary?.tickets?.porEstado || [])
            .filter(x => x.cantidad > 0)
            .map(x => ({ name: x.estado, value: x.cantidad, fill: COLORS_ESTADO[x.estado] || '#6b7280' })),
        [summary]);

    const trendData = useMemo(() => summary?.trendData || [], [summary]);

    const aging = useMemo(() => {
        const a = tiPerf?.aging || {};
        return [
            { label: '0-2 días', value: a.d0_2 || 0, color: '#22c55e' },
            { label: '3-7 días', value: a.d3_7 || 0, color: '#84cc16' },
            { label: '8-14 días', value: a.d8_14 || 0, color: '#f59e0b' },
            { label: '15-30 días', value: a.d15_30 || 0, color: '#f97316' },
            { label: '30+ días', value: a.d30p || 0, color: '#ef4444' },
        ];
    }, [tiPerf]);

    const perAgent = useMemo(() => tiPerf?.perAgent || [], [tiPerf]);
    const tiTrend = useMemo(() => (tiPerf?.trend || []).slice(-14), [tiPerf]);

    // ── Export functions ─────────────────────────────────────────
    const exportarExcel = async (tipo) => {
        try {
            const response = await reportesAPI.exportToExcel(tipo, { startDate: dateRange.start, endDate: dateRange.end, ...filters });
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Reporte_${tipo}_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Error exporting:', e);
            alert('Error al exportar.');
        }
    };

    const generarTrimestral = async () => {
        setGeneratingTrimestral(true);
        try {
            const response = await reportesAPI.getRegistrosTrimestrales(Math.floor(trimestralParams.trimestre), trimestralParams.año);
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Registros_Q${Math.floor(trimestralParams.trimestre)}_${trimestralParams.año}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Error trimestral:', e);
            alert('Error al generar reporte trimestral.');
        } finally {
            setGeneratingTrimestral(false);
        }
    };

    // ── Loading state ────────────────────────────────────────────
    if (!token) return null;

    if (loading && !summary) {
        return (
            <div className="p-6 flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
                    <p className="mt-3 text-gray-500 dark:text-gray-400">Cargando dashboard...</p>
                </div>
            </div>
        );
    }

    // ── Render ────────────────────────────────────────────────────
    return (
        <div className="p-4 md:p-6 space-y-5">
            {/* ── Header ──────────────────────────────────────── */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900 dark:text-white">
                    <BarChart2 className="w-6 h-6 text-primary" />
                    Dashboard de Reportes
                </h1>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1">
                        <select value={periodo} onChange={e => setPeriodo(e.target.value)} aria-label="Periodo"
                            className="text-sm bg-transparent border-none focus:ring-0 dark:text-white pr-7">
                            <option value="total">Total</option>
                            <option value="mes">Por mes</option>
                            <option value="rango">Rango de fechas</option>
                        </select>
                        {periodo === 'mes' && (
                            <input type="month" value={mes} max={mesLocal(new Date())}
                                onChange={e => e.target.value && setMes(e.target.value)}
                                className="text-sm bg-transparent border-none focus:ring-0 dark:text-white w-36" />
                        )}
                        {periodo === 'rango' && (
                            <>
                                <input type="date" value={rango.start}
                                    onChange={e => setRango(p => ({ ...p, start: e.target.value }))}
                                    className="text-sm bg-transparent border-none focus:ring-0 dark:text-white w-32" />
                                <span className="text-gray-400">→</span>
                                <input type="date" value={rango.end}
                                    onChange={e => setRango(p => ({ ...p, end: e.target.value }))}
                                    className="text-sm bg-transparent border-none focus:ring-0 dark:text-white w-32" />
                            </>
                        )}
                    </div>
                    <button onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                        {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        Filtros
                    </button>
                    <button onClick={() => { loadAll(); loadTi(); }}
                        className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* ── Collapsible Filters ─────────────────────────── */}
            {showFilters && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-wrap gap-4 items-end">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Empresa</label>
                        <select value={filters.empresa} onChange={e => setFilters(p => ({ ...p, empresa: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white">
                            <option value="all">Todas</option>
                            {(metadata?.usuarios?.empresas || []).map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                    </div>
                </div>
            )}

            {/* ── KPI Cards ───────────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <KpiCard label="Usuarios" value={stats?.totalUsuarios || 0} icon={Users} color="blue" />
                <KpiCard label="Total Activos" value={stats?.totalActivos || 0} icon={HardDrive} color="green" />
                <KpiCard label="Asignados" value={stats?.activosAsignados || 0} icon={CheckCircle} color="indigo"
                    sub={`${stats?.activosDisponibles || 0} disponibles`} />
                <KpiCard label="Valor del inventario" value={clp(summary?.valor?.totalActivos)} icon={DollarSign} color="green"
                    sub={summary?.valor?.sinValor ? `${summary.valor.sinValor} activos sin valor` : 'Todos con valor'} />
                <KpiCard label="Tickets" value={stats?.totalTickets || 0} icon={Activity} color="purple" />
                <KpiCard label="Tickets Abiertos" value={aging.reduce((s, a) => s + a.value, 0)} icon={AlertTriangle} color="yellow" />
            </div>

            {/* ── Activos y valor del inventario ─────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Activos e Insumos */}
                <Widget title="Activos e Insumos" icon={HardDrive}>
                    {catData.length > 0 ? (
                        <div className="flex items-center gap-4">
                            <div style={{ width: 160, height: 160 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={catData} cx="50%" cy="50%" innerRadius={40} outerRadius={70}
                                            dataKey="value" paddingAngle={2}>
                                            {catData.map((_, i) => <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />)}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex-1 space-y-1.5">
                                {catData.map((c, i) => {
                                    const Icon = catIcons[c.name] || HardDrive;
                                    return (
                                        <div key={i} className="flex items-center justify-between text-sm">
                                            <span className="flex items-center gap-2">
                                                <Icon className="w-3.5 h-3.5" style={{ color: COLORS_PIE[i % COLORS_PIE.length] }} />
                                                <span className="text-gray-600 dark:text-gray-300">{c.name}</span>
                                            </span>
                                            <span className="font-semibold text-gray-900 dark:text-white">{c.value}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : <p className="text-gray-400 text-sm text-center py-8">Sin activos</p>}
                </Widget>

                {/* Valor del inventario */}
                <Widget title="Valor del inventario" icon={DollarSign} span={2}>
                    {(summary?.valor?.porCategoria || []).some(c => c.valor > 0) ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                            {[['Por categoría', 'categoria', summary.valor.porCategoria], ['Por empresa', 'empresa', summary.valor.porEmpresa]].map(([titulo, key, lista]) => {
                                const max = Math.max(1, ...lista.map(x => x.valor));
                                return (
                                    <div key={key}>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">{titulo}</p>
                                        <div className="space-y-1.5">
                                            {lista.filter(x => x.valor > 0).slice(0, 6).map((x, i) => (
                                                <div key={i}>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600 dark:text-gray-300 truncate">{x[key]} <span className="text-gray-400">({x.cantidad})</span></span>
                                                        <span className="font-semibold text-gray-900 dark:text-white">{clp(x.valor)}</span>
                                                    </div>
                                                    <div className="h-1.5 rounded bg-gray-100 dark:bg-gray-700">
                                                        <div className="h-1.5 rounded" style={{ width: `${(x.valor / max) * 100}%`, background: COLORS_PIE[i % COLORS_PIE.length] }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                            {summary.valor.totalInsumos > 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 sm:col-span-2">Stock de insumos valorizado: <b>{clp(summary.valor.totalInsumos)}</b></p>
                            )}
                        </div>
                    ) : <p className="text-gray-400 text-sm text-center py-8">Aún no hay valores cargados en los activos</p>}
                </Widget>

            </div>

            {/* ── Seguridad de equipos (Vicarius) ─────────────── */}
            <ReporteSeguridad />

            {/* ── Satisfacción, SLA vivo y volumen ─────────────── */}
            {tiPerf && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                        <KpiCard label="Satisfacción (CSAT)" icon={ThumbsUp} color="green"
                            value={tiPerf.csat?.promedio != null ? `${tiPerf.csat.promedio} ★` : '—'}
                            sub={`${tiPerf.csat?.respuestas || 0} reseñas`} />
                        <KpiCard label="Respuesta a la encuesta" icon={Activity} color="indigo"
                            value={tiPerf.csat?.tasaRespuestaPct != null ? `${tiPerf.csat.tasaRespuestaPct}%` : '—'}
                            sub="de los tickets resueltos" />
                        <KpiCard label="Fuera de SLA ahora" icon={AlertTriangle} color={tiPerf.fueraSla?.total > 0 ? 'red' : 'green'}
                            value={tiPerf.fueraSla?.total || 0} sub="abiertos sobre su límite" />
                        <KpiCard label="Esperando al usuario" icon={Clock} color="yellow"
                            value={tiPerf.esperandoUsuario?.actuales || 0}
                            sub={tiPerf.esperandoUsuario?.promedioEsperaHoras != null ? `espera prom. ${tiPerf.esperandoUsuario.promedioEsperaHoras} h` : 'ninguno en espera'} />
                        <KpiCard label="No solucionados" icon={RefreshCw} color="purple"
                            value={tiPerf.noSolucionados || 0}
                            sub={tiPerf.ticketsReabiertos ? `${tiPerf.ticketsReabiertos} reabiertos` : 'el usuario pidió revisar'} />
                    </div>

                {/* Rendimiento por Agente */}
                <Widget title="Rendimiento Equipo TI" icon={Users}>
                    {perAgent.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Agente</th>
                                        <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Abiertos</th>
                                        <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Resueltos</th>
                                        <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">1ra Resp.</th>
                                        <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Resolución</th>
                                        <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">SLA Resp.</th>
                                        <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">SLA Res.</th>
                                        <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" title="Tickets abiertos que ya superaron su límite de resolución">Fuera SLA</th>
                                        <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" title="Tickets donde el usuario respondió que no quedó solucionado">No solucionados</th>
                                        <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Rating</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {perAgent.map((a, i) => (
                                        <React.Fragment key={i}>
                                        <tr onClick={() => setAgenteExpandido(agenteExpandido === a.id ? null : a.id)}
                                            title="Ver los tickets de este agente"
                                            className={`border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer ${agenteExpandido === a.id ? 'bg-blue-50/60 dark:bg-blue-900/10' : ''}`}>
                                            <td className="py-2.5 px-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                                                        {(a.nombre || 'U')[0]}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-white text-sm">{a.nombre}</p>
                                                        <p className="text-xs text-gray-400">{a.rol}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-center py-2.5 px-3">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                                    {a.abiertosAsignados}
                                                </span>
                                            </td>
                                            <td className="text-center py-2.5 px-3">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                                    {a.resueltosPeriodo}
                                                </span>
                                            </td>
                                            <td className="text-center py-2.5 px-3 text-gray-600 dark:text-gray-300 text-sm">{a.tiempoPrimeraRespuestaMin == null ? '—' : `${a.tiempoPrimeraRespuestaMin} min`}</td>
                                            <td className="text-center py-2.5 px-3 text-gray-600 dark:text-gray-300 text-sm">{a.tiempoResolucionHoras == null ? '—' : `${a.tiempoResolucionHoras} h`}</td>
                                            <td className="text-center py-2.5 px-3">
                                                <span className={`text-sm font-semibold ${a.slaPrimeraRespuestaPct == null ? 'text-gray-400' : a.slaPrimeraRespuestaPct >= 90 ? 'text-green-600' : a.slaPrimeraRespuestaPct >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                                                    {a.slaPrimeraRespuestaPct == null ? '—' : `${a.slaPrimeraRespuestaPct}%`}
                                                </span>
                                            </td>
                                            <td className="text-center py-2.5 px-3">
                                                <span className={`text-sm font-semibold ${a.slaResolucionPct == null ? 'text-gray-400' : a.slaResolucionPct >= 90 ? 'text-green-600' : a.slaResolucionPct >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                                                    {a.slaResolucionPct == null ? '—' : `${a.slaResolucionPct}%`}
                                                </span>
                                            </td>
                                            <td className="text-center py-2.5 px-3">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${a.fueraSlaAbiertos > 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                                                    {a.fueraSlaAbiertos || 0}
                                                </span>
                                            </td>
                                            <td className="text-center py-2.5 px-3">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${a.noSolucionados > 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                                                    {a.noSolucionados || 0}
                                                </span>
                                            </td>
                                            <td className="text-center py-2.5 px-3">
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); setAgenteResenas(a); }}
                                                    disabled={!a.cantidadFeedbacks}
                                                    title={a.cantidadFeedbacks ? 'Ver reseñas' : 'Sin reseñas en el período'}
                                                    className="rounded px-1.5 py-0.5 enabled:hover:bg-yellow-50 enabled:dark:hover:bg-yellow-900/20 enabled:cursor-pointer disabled:cursor-default"
                                                >
                                                    <span className="text-yellow-500 font-medium text-sm">{a.promedioCalificacion || 0} ★</span>
                                                    <span className="text-xs text-gray-400 ml-0.5">({a.cantidadFeedbacks || 0})</span>
                                                </button>
                                            </td>
                                        </tr>
                                        {agenteExpandido === a.id && (
                                            <tr className="bg-gray-50/70 dark:bg-gray-900/30">
                                                <td colSpan={10} className="px-3"><TicketsAgentePanel agenteId={a.id} /></td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center py-8">
                            {tiLoading ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /> :
                                <p className="text-gray-400 text-sm">Sin datos de rendimiento TI</p>}
                        </div>
                    )}
                </Widget>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        <Widget title="Satisfacción del usuario" icon={ThumbsUp}>
                            {tiPerf.csat?.respuestas > 0 ? (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        {[...(tiPerf.csat.distribucion || [])].reverse().map((d) => {
                                            const pct = tiPerf.csat.respuestas ? (d.total / tiPerf.csat.respuestas) * 100 : 0;
                                            return (
                                                <div key={d.estrellas} className="flex items-center gap-2 text-sm">
                                                    <span className="w-8 text-gray-600 dark:text-gray-300">{d.estrellas} ★</span>
                                                    <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                                                        <div className="h-2 rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
                                                    </div>
                                                    <span className="w-6 text-right font-semibold text-gray-900 dark:text-white">{d.total}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex justify-around pt-3 border-t border-gray-100 dark:border-gray-700 text-center">
                                        <div>
                                            <p className="text-lg font-bold text-gray-900 dark:text-white">{tiPerf.csat.rapidez ?? '—'}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Rapidez</p>
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-gray-900 dark:text-white">{tiPerf.csat.atencion ?? '—'}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Atención</p>
                                        </div>
                                    </div>
                                </div>
                            ) : <p className="text-gray-400 text-sm text-center py-8">Sin reseñas en el período</p>}
                        </Widget>

                        <Widget title="SLA por prioridad" icon={Target}>
                            {Object.keys(tiPerf.porPrioridad || {}).length > 0 ? (
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-gray-700">
                                            <th className="text-left py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Prioridad</th>
                                            <th className="text-center py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Tickets</th>
                                            <th className="text-center py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Resp.</th>
                                            <th className="text-center py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Res.</th>
                                            <th className="text-center py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Vencidos</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {['Crítica', 'Alta', 'Media', 'Baja'].filter((p) => tiPerf.porPrioridad[p]).map((p) => {
                                            const v = tiPerf.porPrioridad[p];
                                            const color = (x) => (x >= 90 ? 'text-green-600' : x >= 70 ? 'text-yellow-600' : 'text-red-600');
                                            return (
                                                <tr key={p} className="border-b border-gray-100 dark:border-gray-700/50">
                                                    <td className="py-2 font-medium text-gray-900 dark:text-white">{p}</td>
                                                    <td className="text-center py-2 text-gray-600 dark:text-gray-300">{v.total}</td>
                                                    <td className={`text-center py-2 font-semibold ${color(v.slaRespuesta)}`}>{v.slaRespuesta}%</td>
                                                    <td className={`text-center py-2 font-semibold ${color(v.slaResolucion)}`}>{v.slaResolucion}%</td>
                                                    <td className={`text-center py-2 font-semibold ${tiPerf.fueraSla?.porPrioridad?.[p] ? 'text-red-600' : 'text-gray-400'}`}>{tiPerf.fueraSla?.porPrioridad?.[p] || 0}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : <p className="text-gray-400 text-sm text-center py-8">Sin datos de SLA</p>}
                            {tiPerf.esperandoUsuario?.pausaPromedioMin != null && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                                    Tiempo promedio esperando al usuario (SLA en pausa): <strong className="text-gray-700 dark:text-gray-200">{tiPerf.esperandoUsuario.pausaPromedioMin} min</strong> por ticket.
                                </p>
                            )}
                        </Widget>

                        <Widget title="Tickets por categoría" icon={Activity}>
                            {(tiPerf.porCategoria || []).length > 0 ? (
                                <div className="space-y-3">
                                    {tiPerf.porCategoria.map((c, i) => {
                                        const max = tiPerf.porCategoria[0].total || 1;
                                        return (
                                            <div key={i}>
                                                <div className="flex items-center justify-between text-sm mb-1">
                                                    <span className="text-gray-600 dark:text-gray-300 truncate pr-2">{c.categoria}</span>
                                                    <span className="font-bold text-gray-900 dark:text-white shrink-0">{c.total} <span className="text-xs font-normal text-gray-400">({c.resueltos} res.)</span></span>
                                                </div>
                                                <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                                                    <div className="h-2 rounded-full" style={{ width: `${(c.total / max) * 100}%`, background: COLORS_PIE[i % COLORS_PIE.length] }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : <p className="text-gray-400 text-sm text-center py-8">Sin tickets en el período</p>}
                        </Widget>
                    </div>

                </>
            )}

            {/* ── Row 1: Charts ───────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Tickets por Estado */}
                <Widget title="Tickets por Estado" icon={Activity}>
                    {ticketEstadoData.length > 0 ? (
                        <div className="flex items-center gap-4">
                            <div style={{ width: 160, height: 160 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={ticketEstadoData} cx="50%" cy="50%" innerRadius={40} outerRadius={70}
                                            dataKey="value" paddingAngle={2}>
                                            {ticketEstadoData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex-1 space-y-1.5">
                                {ticketEstadoData.map((e, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm">
                                        <span className="flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: e.fill }} />
                                            <span className="text-gray-600 dark:text-gray-300">{e.name}</span>
                                        </span>
                                        <span className="font-semibold text-gray-900 dark:text-white">{e.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : <p className="text-gray-400 text-sm text-center py-8">Sin tickets en este período</p>}
                </Widget>

                {/* SLA Cumplimiento */}
                <Widget title="SLA Cumplimiento" icon={Target}>
                    {tiPerf ? (
                        <div className="flex justify-around items-center h-full">
                            <SlaGauge label="Respuesta" value={tiPerf.slaCumplimientoRespuesta || 0} icon={Zap} />
                            <SlaGauge label="Resolución" value={tiPerf.slaCumplimientoResolucion || 0} icon={Timer} />
                        </div>
                    ) : (
                        <div className="flex justify-center items-center h-32">
                            {tiLoading ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /> :
                                <p className="text-gray-400 text-sm">Sin datos SLA</p>}
                        </div>
                    )}
                </Widget>

                {/* Aging Tickets */}
                <Widget title="Antigüedad Tickets Abiertos" icon={Clock}>
                    <div className="space-y-3">
                        {aging.map((b, i) => {
                            const total = aging.reduce((s, a) => s + a.value, 0);
                            const pct = total > 0 ? (b.value / total * 100) : 0;
                            return (
                                <div key={i}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="text-gray-600 dark:text-gray-300">{b.label}</span>
                                        <span className="font-bold text-gray-900 dark:text-white">{b.value}</span>
                                    </div>
                                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                                        <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: b.color }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Widget>
            </div>

            {/* ── Row 2: Tendencia + Aging ────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Tendencia Mensual */}
                <Widget title="Tendencia Mensual" icon={TrendingUp}>
                    {trendData.length > 0 ? (
                        <div style={{ width: '100%', height: 220 }}>
                            <ResponsiveContainer>
                                <AreaChart data={trendData}>
                                    <defs>
                                        <linearGradient id="gradTickets" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="gradAsign" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                                    <Area type="monotone" dataKey="tickets" stroke="#8b5cf6" fill="url(#gradTickets)" name="Tickets" strokeWidth={2} />
                                    <Area type="monotone" dataKey="asignaciones" stroke="#22c55e" fill="url(#gradAsign)" name="Asignaciones" strokeWidth={2} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    ) : <p className="text-gray-400 text-sm text-center py-12">Sin datos de tendencia</p>}
                </Widget>
                {tiPerf && (
                    <>
                        <Widget title="Tickets por día de la semana" icon={TrendingUp}>
                            <div style={{ width: '100%', height: 200 }}>
                                <ResponsiveContainer>
                                    <BarChart data={tiPerf.porDiaSemana || []} barSize={22}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="total" fill="#8b5cf6" name="Tickets" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Widget>
                        <Widget title="Tickets por hora de creación" icon={Clock}>
                            <div style={{ width: '100%', height: 200 }}>
                                <ResponsiveContainer>
                                    <BarChart data={tiPerf.porHora || []} barSize={10}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="hora" tick={{ fontSize: 10 }} tickFormatter={(h) => `${h}h`} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip content={<CustomTooltip />} labelFormatter={(h) => `${h}:00`} />
                                        <Bar dataKey="total" fill="#3b82f6" name="Tickets" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Widget>
                    </>
                )}

            </div>


            {/* ── Row 4: Exports y Evidencias ──────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                {/* Tickets Creados vs Cerrados (diario) */}
                <Widget title="Creados vs Cerrados" icon={TrendingUp}>
                    {tiTrend.length > 0 ? (
                        <div style={{ width: '100%', height: 200 }}>
                            <ResponsiveContainer>
                                <BarChart data={tiTrend} barSize={6}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="fecha" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={50}
                                        tickFormatter={v => v ? v.slice(5) : ''} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                                    <Bar dataKey="creados" fill="#8b5cf6" name="Creados" radius={[2, 2, 0, 0]} />
                                    <Bar dataKey="cerrados" fill="#22c55e" name="Cerrados" radius={[2, 2, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-48">
                            {tiLoading ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /> :
                                <p className="text-gray-400 text-sm">Sin datos de tendencia</p>}
                        </div>
                    )}
                </Widget>
                <Widget title="Exportar Reportes" icon={Download}>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { label: 'General', tipo: 'general', icon: BarChart2 },
                            { label: 'Activos', tipo: 'activos', icon: HardDrive },
                            { label: 'Tickets', tipo: 'tickets', icon: Activity },
                            { label: 'Rendimiento TI', tipo: 'ti', icon: Users },
                        ].map(({ label, tipo, icon: BtnIcon }) => (
                            <button key={tipo} onClick={() => exportarExcel(tipo)}
                                className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-sm font-medium text-gray-700 dark:text-gray-200">
                                <BtnIcon className="w-4 h-4 text-green-600" />
                                <span className="hidden xl:inline">{label}</span>
                                <span className="xl:hidden">{label.split(' ')[0]}</span>
                                <FileText className="w-3.5 h-3.5 ml-auto text-gray-400 hidden sm:block" />
                            </button>
                        ))}
                    </div>
                </Widget>

                <Widget title="Registros Trimestrales" icon={FileText}>
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Trimestre</label>
                                <select value={trimestralParams.trimestre}
                                    onChange={e => setTrimestralParams(p => ({ ...p, trimestre: Number(e.target.value) }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white">
                                    <option value={1}>Q1</option>
                                    <option value={2}>Q2</option>
                                    <option value={3}>Q3</option>
                                    <option value={4}>Q4</option>
                                </select>
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Año</label>
                                <select value={trimestralParams.año}
                                    onChange={e => setTrimestralParams(p => ({ ...p, año: Number(e.target.value) }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white">
                                    {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - 1 + i).map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                        </div>
                        <button onClick={generarTrimestral} disabled={generatingTrimestral}
                            className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors text-sm font-medium flex justify-center items-center gap-2">
                            {generatingTrimestral ? (
                                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Generando...</>
                            ) : (
                                <><Download className="w-4 h-4" /> Descargar Trimestral</>
                            )}
                        </button>
                    </div>
                </Widget>

                <Widget title="Auditoría y Seguridad" icon={Shield}>
                    <div className="flex flex-col items-center justify-center h-full text-center space-y-3 pb-2">
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-full">
                            <Camera className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">Evidencias de Seguridad</p>
                            <p className="text-xs text-gray-500 mt-1 px-2">Acceso al registro fotográfico de sucursales.</p>
                        </div>
                        <button onClick={() => navigate('/evidencias-seguridad')} className="px-4 py-2 w-full max-w-[200px] mt-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors">
                            Ver Evidencias
                        </button>
                    </div>
                </Widget>
            </div>

            {/* ── Métricas adicionales TI ─────────────────────── */}
            {tiPerf && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
                        <Timer className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{tiPerf.tiempoPromedioRespuesta || 0}<span className="text-sm font-normal text-gray-400 ml-1">min</span></p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Promedio 1ra Respuesta</p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
                        <Clock className="w-5 h-5 mx-auto mb-1 text-indigo-500" />
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{tiPerf.tiempoPromedioResolucion || 0}<span className="text-sm font-normal text-gray-400 ml-1">min</span></p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Promedio Resolución</p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
                        <Zap className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{tiPerf.cerradosAutomaticamente || 0}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Cerrados Automáticamente</p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
                        <ThumbsUp className="w-5 h-5 mx-auto mb-1 text-green-500" />
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{tiPerf.totalTickets || 0}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total Tickets (período)</p>
                    </div>
                </div>
            )}

            <ResenasAgenteModal agente={agenteResenas} from={desdeTi} to={dateRange.end} onClose={() => setAgenteResenas(null)} />
        </div>
    );
}
